import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, FormControl, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { WompiRelayService } from '../../../../services/wompi-relay.service';
import { ToastService } from '../../../../services/toast.service';
import { LoadingService } from '../../../../services/loading.service';

@Component({
  selector: 'app-new-wompi-relay',
  templateUrl: './new-wompi-relay.component.html',
  styleUrls: ['./new-wompi-relay.component.scss']
})
export class NewWompiRelayComponent implements OnInit {
  relayForm!: FormGroup;
  readonly isEditing = this.route.snapshot.paramMap.has('id');
  readonly relayId = this.route.snapshot.paramMap.get('id');
  submitting = false;

  constructor(
    private fb: FormBuilder,
    private wompiRelayService: WompiRelayService,
    private toastService: ToastService,
    private router: Router,
    private route: ActivatedRoute,
    private loadingService: LoadingService
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    if (this.isEditing && this.relayId) {
      this.loadRelayUrl();
    }
  }

  private initForm(): void {
    this.relayForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(100)]],
      targetUrl: ['', [Validators.required, Validators.maxLength(500), Validators.pattern(/^https?:\/\/.+/)]],
      description: ['', [Validators.maxLength(500)]],
      isActive: [true],
      headers: this.fb.array([])
    });
  }

  get headers(): FormArray {
    return this.relayForm.get('headers') as FormArray;
  }

  createHeaderFormGroup(headerData?: any): FormGroup {
    return this.fb.group({
      id: [headerData?.id || null],
      headerName: [headerData?.headerName || '', [Validators.required, Validators.maxLength(100)]],
      headerValue: [headerData?.headerValue || '', [Validators.required, Validators.maxLength(500)]],
      isActive: [headerData?.isActive !== undefined ? headerData.isActive : true]
    });
  }

  addHeader(): void {
    this.headers.push(this.createHeaderFormGroup());
  }

  removeHeader(index: number): void {
    this.headers.removeAt(index);
  }

  private loadRelayUrl(): void {
    this.loadingService.show();
    
    this.wompiRelayService.getById(Number(this.relayId)).subscribe({
      next: ({ data }) => {
        this.relayForm.patchValue({
          name: data.name,
          targetUrl: data.targetUrl,
          description: data.description,
          isActive: data.isActive
        });

        // Cargar headers existentes
        if (data.headers && data.headers.length > 0) {
          data.headers.forEach((header: any) => {
            this.headers.push(this.createHeaderFormGroup(header));
          });
        }
        
        this.loadingService.hide();
      },
      error: (error) => {
        console.error('Error loading relay URL:', error);
        this.toastService.showToast('error', 'Error al cargar la URL de relay');
        this.loadingService.hide();
        this.router.navigate(['/dashboard/config/wompi-relay']);
      }
    });
  }

  onSubmit(): void {
    if (this.relayForm.invalid) {
      this.relayForm.markAllAsTouched();
      this.toastService.showToast('error', 'Por favor, complete todos los campos requeridos correctamente');
      return;
    }

    if (this.submitting) return;

    this.submitting = true;
    this.loadingService.show();

    const formValue = this.relayForm.getRawValue();

    const serviceCall = this.isEditing && this.relayId
      ? this.wompiRelayService.update(Number(this.relayId), formValue)
      : this.wompiRelayService.insert(formValue);

    serviceCall.subscribe({
      next: () => {
        const action = this.isEditing ? 'actualizada' : 'creada';
        this.toastService.showToast('success', `URL de relay ${action} exitosamente`);
        this.router.navigate(['/dashboard/config/wompi-relay']);
      },
      error: (error) => {
        console.error('Error submitting relay URL:', error);
        const action = this.isEditing ? 'actualizar' : 'crear';
        this.toastService.showToast('error', `Error al ${action} la URL de relay`);
        this.submitting = false;
        this.loadingService.hide();
      },
      complete: () => {
        this.submitting = false;
        this.loadingService.hide();
      }
    });
  }

  hasError(controlName: string, errorType: string): boolean {
    const control = this.relayForm.get(controlName);
    return !!(control && (control.touched || control.dirty) && control.hasError(errorType));
  }

  hasHeaderError(index: number, controlName: string, errorType: string): boolean {
    const headerControl = this.headers.at(index).get(controlName);
    return !!(headerControl && (headerControl.touched || headerControl.dirty) && headerControl.hasError(errorType));
  }

  cancel(): void {
    this.router.navigate(['/dashboard/config/wompi-relay']);
  }
}

